function toStringOrEmpty(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function yesNoFromBool(value) {
  if (value === true) return "YES";
  if (value === false) return "NO";
  return "";
}

function mapApplicantToBorrower(applicant) {
  const financial = applicant?.financial_info || {};
  const extracted = applicant?.extracted_data || {};

  return {
    id: applicant?.id,
    role: applicant?.applicant_role,
    personal_info: {
      first_name: applicant?.first_name || "",
      last_name: applicant?.last_name || "",
      date_of_birth: applicant?.date_of_birth || "",
      nationality: applicant?.nationality || "",
      country_of_residence: applicant?.country_of_residence || "",
      residency_status: applicant?.residency_status || "",
      national_id_number: applicant?.national_id_number || "",
      address: applicant?.official_address || "",
      email: applicant?.email || "",
      phone: applicant?.phone || "",
      marital_status: applicant?.marital_status || "",
      dependents:
        applicant?.dependents === null || applicant?.dependents === undefined
          ? ""
          : applicant.dependents,
      disability:
        applicant?.disability === null || applicant?.disability === undefined
          ? 0
          : applicant.disability,
      preferred_contact_method: applicant?.preferred_contact_method || "EMAIL",
      notes: applicant?.notes || "",
    },
    financial_info: {
      net_income: toStringOrEmpty(financial?.net_monthly_income),
      listing_in_cpk: financial?.credit_listing_status || "",
      monthly_expenses: toStringOrEmpty(financial?.current_monthly_expenses),
      current_rent: toStringOrEmpty(financial?.current_rent),
      employment_status: financial?.employment_status || "",
      contract_type: financial?.contract_type || "",
      employment_contracts_count: toStringOrEmpty(
        financial?.num_employment_contracts,
      ),
      politically_exposed_person:
        financial?.politically_exposed_person_status || "",
      notes: financial?.notes || "",
    },
    documents: {},
    extracted_data: {
      country_code: extracted?.country_code || "",
      extraction_date: extracted?.extraction_date || "",
      missing_fields_count: extracted?.missing_fields_count ?? "",
      model_confidence_avg: toStringOrEmpty(extracted?.model_confidence_avg),
      model_field_accuracy: toStringOrEmpty(extracted?.model_field_accuracy),
      field_consistency: toStringOrEmpty(extracted?.field_consistency),
      issuing_authority: extracted?.issuing_authority || "",
      identity_data: extracted?.identity_data || {},
      income_data: extracted?.income_data || {},
      bank_statement_data: extracted?.bank_statement_data || {},
      tax_declaration_data: extracted?.tax_declaration_data || {},
    },
  };
}

function buildBorrowerDocumentsMap(documentRequirements, applicantId) {
  const relevant = (documentRequirements || []).filter(
    (req) => req?.dossier_applicant_id === applicantId,
  );

  return Object.fromEntries(
    relevant.map((req) => [
      req.document_type_code,
      {
        requirement_id: req.id,
        document_type_code: req.document_type_code,
        document_type_label: req.document_type_label,
        scope: req.dossier_applicant_role ? "APPLICANT" : "DOSSIER",
        is_required: req.is_required,
        status: req.status,
        has_active_version: (req.versions || []).some((v) => v?.is_active),
        versions: req.versions || [],
      },
    ]),
  );
}

function buildRequirementMapByCode(documentRequirements) {
  const result = {};

  (documentRequirements || []).forEach((req) => {
    const code = req.document_type_code;

    if (req.dossier_applicant_role) {
      if (!result[code] || typeof result[code] !== "object") {
        result[code] = {};
      }

      result[code][req.dossier_applicant_role] = req.id;
    } else {
      result[code] = req.id;
    }
  });

  return result;
}

export function mapDossierDetailToDraft(dossier) {
  const borrowers = Array.isArray(dossier?.borrowers)
    ? dossier.borrowers
    : (dossier?.applicants || []).map(mapApplicantToBorrower);

  const primaryBorrower =
    borrowers.find((b) => b?.role === "PRIMARY") || borrowers[0] || null;

  const coBorrower = borrowers.find((b) => b?.role === "CO_APPLICANT") || null;

  return {
    dossier_id: dossier?.dossier_id || dossier?.id || null,
    dossier_code: dossier?.dossier_code || dossier?.code || null,

    dossier_applicant_id:
      dossier?.primary_dossier_applicant_id || primaryBorrower?.id || null,

    primary_dossier_applicant_id:
      dossier?.primary_dossier_applicant_id || primaryBorrower?.id || null,

    co_dossier_applicant_id:
      dossier?.co_dossier_applicant_id || coBorrower?.id || null,

    status: dossier?.status || "DRAFT",

    country_of_property_purchase:
      dossier?.country_of_property_purchase ||
      dossier?.market_region?.country_code ||
      "",

    market_region_id:
      dossier?.market_region_id || dossier?.market_region?.id || null,

    market_region: dossier?.market_region || null,

    borrowers,
    completed_steps: dossier?.completed_steps || {},
    document_requirements: dossier?.document_requirements || [],

    document_requirement_map_by_code:
      dossier?.document_requirement_map_by_code ||
      buildRequirementMapByCode(dossier?.document_requirements || []),

    dossier_financial_info: dossier?.dossier_financial_info ||
      dossier?.financing_details || {
        reuse_simulation_file_name: "",
        total_loan_amount: "",
        renovation_loan_amount: "",
        equity_amount: "",
        property_price: "",
        tenure_years: 9,
        monthly_cost_percent: 40,
        estimated_monthly_payment: "",
        notary_of_choice: "",
        repayment_bank_account: "",
        loan_disbursement_bank_account: "",
        notes: "",
      },

    property_information: dossier?.property_information ||
      dossier?.linked_property || {
        property_id: null,
        prefilled_from_existing_property: false,
        country: "",
        photos: [],
      },

    renovation_cost_estimate: dossier?.renovation_cost_estimate || null,
    financial_indicators: dossier?.financial_indicators || null,

    review_download: dossier?.review_download || {},

    requires_reextraction: false,
  };
}
